// Which of an Ongoing's LINKED THREADS get a node on the story rail, and what each one is.
//
// The story answers with every thread linked to the ongoing under one field, `emails` — a historic
// misnomer the service keeps on purpose: the threads carrying a task, a note or an event arrive there
// too, and dropping them once erased most of a research story. So the rail used to draw every one of
// them as an email, which is the bug Pouria reported (2026-08-26): "we just create a task, and from
// that task an ongoing is created — NOTHING should be attached to it". Creating a task links that
// task's own thread to the new ongoing (that is how the story knows its due date), and the rail then
// showed it back as an attached email.
//
// timeline-srv now says what each thread IS (`kind`), so two rules can be stated here, away from the
// rendering:
//
//   • an EMAIL thread is a node, as it always was — real mail attached to the story;
//   • any other thread is a node unless one of the story's own TIMELINE STEPS already stands for that
//     entity — an entity created inside the story is a step, and a second node for it would say the
//     same thing twice.
//
// The origin HEADER deliberately does not count as "already shown". It was suppressing the node at
// first, and the header is not openable, so the one task a fresh ongoing is made of could be seen and
// not opened — Pouria's follow-up: "after we create task and create ongoing I can't open the task —
// it should be same as the other". The entity keeps its own node, which opens exactly as a task step
// does, and lib/storySteps.js drops the now-redundant header (the same rule an email-origin story has
// always followed once its mail nodes are on the rail).
//
// Kept as plain functions on plain data: the rail, the side panel and the tests all ask the same
// question and must get the same answer.

// `message` (busy-timeline-srv#29): a conversation from a source adapter — SMS, WhatsApp, a call.
// A node like an email's, never "stepped away": a message thread is real traffic attached to the
// story, and no step of the story's own stands for it.
const KINDS = ['email', 'task', 'note', 'event', 'file', 'message']

/**
 * What a linked thread is: 'email' | 'task' | 'note' | 'event' | 'file' | 'message'.
 *
 * Anything missing or unrecognised reads as an email — that is what an unclassified linked thread has
 * always been drawn as, and an older server that doesn't send `kind` must keep behaving as it did.
 */
export function threadKind(thread) {
  const kind = String(thread?.kind || '').trim().toLowerCase()
  return KINDS.includes(kind) ? kind : 'email'
}

/** The entity title carried in a step label ("Note added: Lock sizes" → "Lock sizes"). */
function labelTitle(label) {
  const s = String(label || '')
  return (s.includes(': ') ? s.slice(s.indexOf(': ') + 2) : s).trim()
}

function norm(text) {
  return String(text || '').trim().toLowerCase()
}

/**
 * The entities the story's own TIMELINE STEPS already stand for — by id where a step names one (the
 * exact answer) and by entity name otherwise (older steps carry no id).
 */
export function steppedEntities(story) {
  const ids = new Set()
  const titles = new Set()
  for (const e of story?.timeline || []) {
    if (e?.entityId) ids.add(String(e.entityId).toLowerCase())
    const title = norm(labelTitle(e?.label))
    if (title) titles.add(title)
  }
  return { ids, titles }
}

/** Is this thread's entity already on the rail as a step? */
export function threadIsStepped(thread, stepped) {
  const id = thread?.entityId ? String(thread.entityId).toLowerCase() : ''
  if (id && stepped.ids.has(id)) return true
  return stepped.titles.has(norm(thread?.subject))
}

/**
 * The linked threads that deserve their own node on the rail, in the order they were given.
 *
 * Emails always qualify. A task/note/event/file thread qualifies unless one of the story's steps is
 * already that entity — so the task an ongoing was created FROM keeps a node (nothing else on the
 * rail can be clicked to open it), and a task created INSIDE the story is not drawn twice.
 */
export function storyThreadNodes(story) {
  const stepped = steppedEntities(story)
  return (story?.emails || [])
    .filter(t => t?.timelineId)
    .filter(t => isConversation(t) || !threadIsStepped(t, stepped))
}

/** A thread that IS traffic — mail, or a message conversation — always keeps its node. */
export function isConversation(thread) {
  const kind = threadKind(thread)
  return kind === 'email' || kind === 'message'
}

/**
 * Is this thread an ENTITY the input box can open (a task, a note or an event with an id)? Mail,
 * files and older payloads that carry no id are not: nothing would open.
 */
export function threadOpensEntity(thread) {
  return ['task', 'note', 'event'].includes(threadKind(thread)) && !!thread?.entityId
}

/** The SmartBox menu each entity kind opens under — one vocabulary, as `editOf`'s (lib/storySteps). */
export const ENTITY_THREAD_MENU = { task: 'tasks', note: 'notes', event: 'events' }

/**
 * The entity id a thread's own TIMELINE record holds, for a node whose payload named none
 * (busy-my-web#267).
 *
 * `threadOpensEntity` above is the happy path: the story says which task/note/event a thread carries
 * and the node opens it. When it does not — an older story, a thread whose items the server could not
 * classify — the node used to carry no opener at all, and for an EVENT there was nothing to expand
 * either (a thread's `dueDate` is the email notion of one, and an event's start never lands in it).
 * That is a row drawn as an event that answers no click of any kind, which is exactly what was
 * reported.
 *
 * The timeline record the rail already reads for an email node knows the answer: the thread's own
 * live task / note / event. The FIRST live one, because a thread is classified by the one kind that
 * decides how it reads (TimelineSrv's StoryThreadKind) — if there are two of a kind on one thread the
 * node stands for the thread, and its first item is the one the node is titled after.
 *
 * Pure over the record so it can be tested without a store: null when there is nothing to open, and
 * the caller then leaves the row exactly as it was rather than promising an opener it does not have.
 */
export function timelineEntityId(timeline, kind) {
  const list = { task: timeline?.tasks, note: timeline?.notes, event: timeline?.events }[String(kind || '')]
  return (list || []).find(x => x && x.id && x.isDeleted !== true)?.id || null
}

/**
 * How many tasks / notes / events the story shows as THREAD nodes rather than as steps.
 *
 * The briefing's counts hold to one invariant — a count equals what its own filter reveals — and its
 * filter reads the rail. A task that is on the rail as its thread (the one a story was created from)
 * is therefore counted here, or the briefing would stay silent about the only thing in the story.
 */
export function entityThreadCounts(story) {
  const counts = { task: 0, note: 0, event: 0 }
  for (const t of storyThreadNodes(story)) {
    const kind = threadKind(t)
    if (kind in counts) counts[kind] += 1
  }
  return counts
}

/**
 * The threads that are REAL MAIL. Every "N emails" the app says is counted from here — the briefing
 * row, the connections panel's summary — so a story with one task and no mail never claims an email.
 */
export function emailThreads(story) {
  return (story?.emails || []).filter(t => t && threadKind(t) === 'email')
}

/** How many emails the story actually has. */
export function emailThreadCount(story) {
  return emailThreads(story).length
}
